import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useReso } from '../../../../../contexts/ResoContext';
import { useMetadata } from '../../../../../contexts/MetadataContext';
import {
  dataURItoBlob,
  downscaleImage,
  getExifInfo,
  ImageUtils,
} from '../../utils/imageUtils';
import {
  BatchProcessedImageData,
  ProcessedImageData,
} from '../../ImageEditDialog';
import { MediaResource } from '@brightmls/nextgen.lae.flc';
import { useService } from '../../../../../contexts/ServiceContext';
import {
  BatchReplaceResult,
  MEDIA_TIMEOUT,
  MediaMetadata,
  MediaUploadResult,
  UploadFileStatus,
  UploadProgress,
  UploadStage,
  UseMediaUploadOptions,
} from './types';
import {
  getBase64Data,
  getMediaShortDescription,
  getMediaSourceFileName,
  getStageStatusText,
  isMediaResourceValid,
  retryWithExponentialBackoff,
} from './utils';
import {
  isMimeTypeNullError,
  translateMediaRecord,
} from '../../../../../system/media';

export function useMediaUpload(options: UseMediaUploadOptions) {
  const {
    listingKey,
    listingDetails,
    onUploadComplete,
    onBatchComplete,
    onUploadError,
    onReplaceComplete,
    onReplaceBatchComplete,
    onReplaceError,
    pollInterval = 500,
    maxPollAttempts = 120,
    imageConfig = {
      maxWidth: ImageUtils.TARGET_WIDTH,
      maxHeight: ImageUtils.TARGET_HEIGHT,
      quality: ImageUtils.JPEG_COMPRESSION,
    },
    maxShadowFailures = 5,
    logger,
    onMimeTypeError,
  } = options;

  // Track media items being polled - using ref instead of state for immediate access
  const pendingMediaPollsRef = useRef<
    Record<
      string,
      {
        mediaKey: string;
        metadata: MediaMetadata;
        startTime: number;
        mediaResource?: MediaResource;
      }
    >
  >({});

  // Track if batch polling is active
  const [isBatchPollingActive, setIsBatchPollingActive] = useState(false);

  // Batch polling interval reference
  const batchPollingIntervalRef = useRef<number | null>(null);

  // Logger reference for consistent logging
  const loggerRef = useRef<any>(null);

  // Refs to store progress simulation intervals
  const progressIntervals = useRef<Record<string, number>>({});

  // Track shadow failure counts per media item
  const shadowFailureCountsRef = useRef<Record<string, number>>({});

  // Track current operation type for appropriate callback selection
  const currentOperationTypeRef = useRef<'upload' | 'replace' | null>(null);

  const { executeQuery } = useReso();
  const { locale } = useMetadata();
  const [, setActiveUploads] = useState(new Set<string>());
  const {
    isRequestInProgress,
    getCurrentRequestPromise,
    registerRequestOperation,
  } = useService();
  const [isAnyUploading, setIsAnyUploading] = useState(false);

  const lastUpdateRef = useRef<Record<string, number>>({});

  const [uploadFileStatuses, setUploadFileStatuses] = useState<
    Record<string, UploadFileStatus>
  >({});

  const [batchSummary, setBatchSummary] = useState({
    totalFiles: 0,
    completedFiles: 0,
    inProgressFiles: new Set<string>(),
  });

  const uploadProgress: UploadProgress = useMemo(() => {
    const currentFileProgress = Array.from(batchSummary.inProgressFiles)
      .sort() // Sort to provide deterministic "current file" logic
      .map((key) => uploadFileStatuses[key]);

    return {
      totalFiles: batchSummary.totalFiles,
      completedFiles: batchSummary.completedFiles,

      // Determine the actively processed file
      currentFile: currentFileProgress[0] || {
        name: '',
        previewUrl: '',
        stage: 'preparing' as UploadStage,
        progress:
          batchSummary.totalFiles > 0 &&
          batchSummary.completedFiles === batchSummary.totalFiles
            ? 100
            : 0,
        statusText: '',
        error: undefined,
      },

      // Derive remaining files (files that have started processing/are next in line)
      remainingFiles: Array.from(batchSummary.inProgressFiles).filter(
        (fileId) => uploadFileStatuses[fileId]?.stage !== 'waiting',
      ),

      // Derive failed files
      failedFiles: Object.values(uploadFileStatuses).filter(
        (status) => status.stage === 'error',
      ) as UploadFileStatus[],
    };
  }, [
    batchSummary.totalFiles,
    batchSummary.completedFiles,
    batchSummary.inProgressFiles,
    uploadFileStatuses, // Dependency on the entire map ensures re-calculation when any file status changes
  ]);

  const translation = locale();

  // Helper function to update a single file's status
  const updateFileStatus = useCallback(
    (
      fileIdentifier: string,
      update: Partial<UploadFileStatus>,
      shouldClearInterval: boolean = false,
    ) => {
      setUploadFileStatuses((prev) => {
        const current = prev[fileIdentifier] || {
          name: fileIdentifier,
          previewUrl: '',
          stage: 'preparing' as UploadStage,
          progress: 0,
        };

        const newStatus = {
          ...current,
          ...update,
          statusText: update.stage
            ? getStageStatusText(update.stage)
            : update.statusText || current.statusText,
        };

        return { ...prev, [fileIdentifier]: newStatus };
      });

      if (shouldClearInterval) {
        if (progressIntervals.current[fileIdentifier]) {
          clearInterval(progressIntervals.current[fileIdentifier]);
          delete progressIntervals.current[fileIdentifier];
        }
      }
    },
    [progressIntervals.current],
  );

  /**
   * Create media record with 30-second timeout
   */
  const createMediaRecord = useCallback(
    async (payload: any): Promise<any> => {
      try {
        return await executeQuery<any>('CWA_Media', {
          method: 'POST',
          body: payload,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('createMediaRecord failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'create');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Upload media content with 30-second timeout
   */
  const uploadMediaContent = useCallback(
    async (mediaKey: string, blob: Blob, contentType: string): Promise<any> => {
      try {
        return await executeQuery(`CWA_Media('${mediaKey}')/$value`, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType,
          },
          buffer: true,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('uploadMediaContent failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'upload');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Replace media content with 30-second timeout
   */
  const replaceMediaContent = useCallback(
    async (
      mediaKey: string,
      blob: Blob | null,
      contentType: string | undefined,
    ): Promise<any> => {
      try {
        return await executeQuery(`CWA_Media('${mediaKey}')/$value`, {
          method: 'PUT',
          body: blob,
          headers: {
            'Content-Type': contentType,
          },
          buffer: true,
          timeout: MEDIA_TIMEOUT,
        });
      } catch (error) {
        console.error('replaceMediaContent failed:', error);
        if (isMimeTypeNullError(error)) {
          onMimeTypeError?.(error as Error, 'replace');
        }
        throw error;
      }
    },
    [executeQuery, onMimeTypeError],
  );

  /**
   * Helper function to wait for any active request operations to complete
   */
  const waitForRequestCompletion = useCallback(
    async (
      operationName: string,
      mediaItems: MediaMetadata[] | ProcessedImageData[],
    ): Promise<void> => {
      if (isRequestInProgress()) {
        const firstItem = mediaItems[0];
        const fileIdentifier = firstItem?.file?.name || '';

        if (fileIdentifier) {
          updateFileStatus(fileIdentifier, {
            stage: 'waiting',
            progress: 0,
            statusText: 'Waiting for other operations to complete...',
          });
          startProgressSimulation(fileIdentifier, 'waiting', 0, 5, 2000); // Small sim for visibility
        }

        const requestPromise = getCurrentRequestPromise();

        if (requestPromise) {
          const log = loggerRef.current?.log || console.log;
          log(
            `Waiting for request operation to complete before ${operationName}`,
          );

          try {
            await requestPromise;
            log(
              `Request operation completed, proceeding with ${operationName}`,
            );
          } catch (requestError) {
            // Log the request error but continue with operation
            log(
              `Request operation failed, but continuing with ${operationName}:`,
              requestError,
            );
          }
        }
      }
    },
    [isRequestInProgress, getCurrentRequestPromise, updateFileStatus],
  );

  /**
   * Clear all progress simulation intervals to prevent memory leaks
   */
  const clearAllIntervals = useCallback(() => {
    Object.values(progressIntervals.current).forEach((interval) => {
      clearInterval(interval);
    });
    progressIntervals.current = {};

    // Also clear batch polling interval
    if (batchPollingIntervalRef.current) {
      clearInterval(batchPollingIntervalRef.current);
      batchPollingIntervalRef.current = null;
    }

    // Clear shadow failure counts when clearing all intervals
    shadowFailureCountsRef.current = {};
  }, []);

  const finalizeFile = useCallback((fileIdentifier: string) => {
    setBatchSummary((prev) => {
      const nextInProgress = new Set(prev.inProgressFiles);

      // Only finalize if the file was actually being tracked
      if (nextInProgress.has(fileIdentifier)) {
        nextInProgress.delete(fileIdentifier);
        const allDone = nextInProgress.size === 0;

        if (allDone) {
          setIsAnyUploading(false);
        }

        return {
          ...prev,
          completedFiles: prev.completedFiles + 1,
          inProgressFiles: nextInProgress,
        };
      }

      return prev; // Return previous state if file wasn't tracked as in-progress
    });
  }, []);

  /**
   * Start a progress simulation interval for the current file
   */
  const startProgressSimulation = useCallback(
    (
      fileIdentifier: string,
      fromStage: UploadStage,
      fromProgress: number,
      toProgress: number,
      duration: number = 10000,
    ) => {
      // Clear any existing interval
      if (progressIntervals.current[fileIdentifier]) {
        clearInterval(progressIntervals.current[fileIdentifier]);
      }

      const steps = Math.max(10, Math.floor(duration / 100));
      const progressPerStep = (toProgress - fromProgress) / steps;
      let currentStep = 0;

      const updateIntervalMs = Math.floor(duration / steps); // Typically 100ms
      const renderLimitMs = 100;

      // Initialize the last update time for this file
      lastUpdateRef.current[fileIdentifier] = Date.now();

      const intervalId = window.setInterval(
        () => {
          const now = Date.now();
          const shouldUpdateUI =
            now - lastUpdateRef.current[fileIdentifier] >= renderLimitMs;

          currentStep++;
          const newProgress = Math.min(
            fromProgress + currentStep * progressPerStep,
            toProgress,
          );

          const isComplete = currentStep >= steps || newProgress >= toProgress;

          if (shouldUpdateUI || isComplete) {
            setUploadFileStatuses((prev) => {
              if (prev[fileIdentifier]?.stage !== fromStage || isComplete) {
                clearInterval(intervalId);
                delete progressIntervals.current[fileIdentifier];
                delete lastUpdateRef.current[fileIdentifier];

                // Ensure final state is 100% if completed
                if (isComplete) {
                  return {
                    ...prev,
                    [fileIdentifier]: {
                      ...prev[fileIdentifier],
                      progress: toProgress,
                      statusText: getStageStatusText(fromStage),
                    },
                  };
                }
                return prev;
              }

              // B. Regular UI Update: Update state with the calculated progress.
              lastUpdateRef.current[fileIdentifier] = now;

              return {
                ...prev,
                [fileIdentifier]: {
                  ...prev[fileIdentifier],
                  progress: newProgress,
                  statusText: `${getStageStatusText(fromStage)} (${Math.round(newProgress)}%)`,
                },
              };
            });
          }
        },
        updateIntervalMs, // Run the simulation loop at the intended frequency (e.g., 100ms)
      );

      progressIntervals.current[fileIdentifier] = intervalId;
    },
    [setUploadFileStatuses],
  );

  /**
   * Handles batch polling for media processing status
   */
  const startBatchPolling = useCallback(() => {
    const log = loggerRef.current?.log || console.log;
    const error = loggerRef.current?.error || console.error;

    // Prevent multiple intervals being created
    if (batchPollingIntervalRef.current) {
      log('Clearing existing batch polling interval');
      clearInterval(batchPollingIntervalRef.current);
      batchPollingIntervalRef.current = null;
    }

    // Mark as active immediately
    setIsBatchPollingActive(true);

    // Create a robust polling function
    const doBatchPoll = async () => {
      try {
        const pendingItems = pendingMediaPollsRef.current;
        const pendingCount = Object.keys(pendingItems).length;

        // Exit if no items to process
        if (pendingCount === 0) {
          log('No pending media items, stopping batch polling');
          setIsBatchPollingActive(false);
          // Also set isAnyUploading to false when we have no pending items
          setIsAnyUploading(false);

          if (currentOperationTypeRef.current === 'replace') {
            onReplaceBatchComplete?.();
          } else {
            onBatchComplete?.();
          }

          // Reset operation type
          currentOperationTypeRef.current = null;

          if (batchPollingIntervalRef.current) {
            clearInterval(batchPollingIntervalRef.current);
            batchPollingIntervalRef.current = null;
          }
          return;
        }

        log(`Running batch poll for ${pendingCount} media items`);
        log(`Current pending items: ${Object.keys(pendingItems).join(', ')}`);

        // Fetch all media for this listing in one request
        const result = await executeQuery<any>('CWA_Media', {
          queryParams: {
            $filter: `CWAResourceRecordKey eq '${listingKey}'`,
            $format: 'json',
          },
        });

        log('CWA_Media Polling query result:', result);

        if (!result || !result.value || !Array.isArray(result.value)) {
          error('Invalid response from batch media query', result);
          return;
        }

        log(`Retrieved ${result.value.length} media items in batch query`);

        // Process each pending media item
        const currentTime = Date.now();
        const completedItems: string[] = [];

        for (const [fileName, pollInfo] of Object.entries(pendingItems)) {
          const { mediaKey, metadata, startTime } = pollInfo;

          // Find this media in the results
          const mediaItem = result.value.find(
            (item: MediaUploadResult) => item.CWAMEDVWKey === mediaKey,
          );

          // Check if this item has timed out
          const hasTimedOut =
            currentTime - startTime > maxPollAttempts * pollInterval;

          if (hasTimedOut) {
            log(
              `Media ${fileName} processing timed out after ${(currentTime - startTime) / 1000}s`,
            );

            updateFileStatus(
              fileName,
              {
                stage: 'error',
                error: 'Upload timed out waiting for processing',
                progress: 0,
              },
              true,
            );

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(fileName);
              return next;
            });

            // Call error handler
            onUploadError?.(new Error('Upload timed out'), metadata);

            finalizeFile(fileName);
            completedItems.push(fileName);
            continue;
          }

          // Skip if not processed yet
          if (
            !mediaItem ||
            mediaItem.CWAMediaProcessingStatus !== 'PROCESSED'
          ) {
            log(
              `Media ${fileName} not yet processed, continuing to monitor...`,
            );
            continue;
          }

          // Media is processed! Validate and complete
          const translatedResult = translateMediaRecord(mediaItem, translation);

          // Initialize shadow failure count if not exists
          if (!shadowFailureCountsRef.current[fileName]) {
            shadowFailureCountsRef.current[fileName] = 0;
          }

          if (isMediaResourceValid(translatedResult)) {
            log(`Media ${fileName} successfully processed!`);

            updateFileStatus(
              fileName,
              { stage: 'complete', progress: 100 },
              true, // clear interval
            );

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(fileName);
              return next;
            });

            if (currentOperationTypeRef.current === 'replace') {
              onReplaceComplete?.(translatedResult);
            } else {
              onUploadComplete?.(translatedResult);
            }
            finalizeFile(fileName);
            completedItems.push(fileName);
          } else {
            // Increment shadow failure count
            shadowFailureCountsRef.current[fileName]++;

            // Lets emit this to the MEDIA debug log (this 'shadow' failure should never happen -- but it does)
            log(
              `Shadow failure increment for media ${fileName}, ${shadowFailureCountsRef.current[fileName]}/${maxShadowFailures}... Keys on MEDIA: ${JSON.stringify(
                Object.keys(translatedResult),
              )}`,
            );

            if (shadowFailureCountsRef.current[fileName] >= maxShadowFailures) {
              // Shadow failure handling
              log(
                `Shadow failure detected for media ${fileName}, cleaning up...`,
              );

              try {
                await executeQuery(`CWA_Media('${mediaKey}')`, {
                  method: 'DELETE',
                });
                log(`Cleaned up invalid media ${fileName}`);
              } catch (deleteError) {
                error('Failed to clean up invalid media:', deleteError);
              }

              updateFileStatus(
                fileName,
                {
                  stage: 'error',
                  error: 'Upload succeeded but media is invalid',
                  statusText: 'Invalid media resource',
                  progress: 0,
                },
                true, // clear interval
              );

              setActiveUploads((prev) => {
                const next = new Set(prev);
                next.delete(fileName);
                return next;
              });

              onUploadError?.(new Error('Media validation failed'), metadata);
              finalizeFile(fileName);
              completedItems.push(fileName);
            } else {
              // Continue polling if not reached max attempts
              log(
                `Continuing to poll for media (attempt ${shadowFailureCountsRef.current[fileName]} of ${maxShadowFailures})`,
              );
            }
          }
        }

        // Process completed items
        if (completedItems.length > 0) {
          log(
            `Completed ${completedItems.length} media items in this batch poll`,
          );

          // Remove completed items from pending
          completedItems.forEach((key) => {
            delete pendingMediaPollsRef.current[key];

            // Also clean up shadow failure counts for completed items
            delete shadowFailureCountsRef.current[key];
          });

          // Check if all items are done
          const remainingCount = Object.keys(
            pendingMediaPollsRef.current,
          ).length;
          if (remainingCount === 0) {
            log('All media items processed, stopping batch polling');
            setIsBatchPollingActive(false);
            // Update isAnyUploading when all items are processed
            setIsAnyUploading(false);
            if (currentOperationTypeRef.current === 'replace') {
              onReplaceBatchComplete?.();
            } else {
              onBatchComplete?.();
            }

            currentOperationTypeRef.current = null;

            if (batchPollingIntervalRef.current) {
              clearInterval(batchPollingIntervalRef.current);
              batchPollingIntervalRef.current = null;
            }
          } else {
            log(`Still monitoring ${remainingCount} pending items`);
          }
        }
      } catch (err) {
        // Handle errors but continue polling
        const errMsg = err instanceof Error ? err.message : String(err);
        error('Error in batch polling:', errMsg);
      }
    };

    // Execute the first poll immediately
    log('Executing first batch poll');
    doBatchPoll();

    // Set up interval for subsequent polls - this is the critical part!
    log(`Setting up batch polling interval (${pollInterval}ms)`);
    batchPollingIntervalRef.current = window.setInterval(
      doBatchPoll,
      pollInterval,
    );

    // Return cleanup function
    return () => {
      if (batchPollingIntervalRef.current) {
        log('Cleaning up batch polling interval');
        clearInterval(batchPollingIntervalRef.current);
        batchPollingIntervalRef.current = null;
      }
      setIsBatchPollingActive(false);
      // Also update isAnyUploading in cleanup
      setIsAnyUploading(false);
      log('Batch polling stopped');
    };
  }, [
    executeQuery,
    finalizeFile,
    isMediaResourceValid,
    listingKey,
    maxPollAttempts,
    onBatchComplete,
    onReplaceBatchComplete,
    onUploadComplete,
    onUploadError,
    pollInterval,
    translation,
    updateFileStatus,
  ]);

  const addToBatchPolling = useCallback(
    (
      mediaKey: string,
      metadata: MediaMetadata,
      operationType: 'upload' | 'replace' = 'upload',
      fileIdentifier: string,
    ) => {
      const log = loggerRef.current?.log || console.log;

      // Update the ref directly
      pendingMediaPollsRef.current[fileIdentifier] = {
        mediaKey,
        metadata,
        startTime: Date.now(),
        // operationType,
        // fileIdentifier,
      };

      currentOperationTypeRef.current = operationType;

      log(
        `Added ${metadata.name} to batch polling queue (mediaKey: ${mediaKey}, operation: ${operationType})`,
      );

      // Only start if not already running
      if (!isBatchPollingActive && !batchPollingIntervalRef.current) {
        log('Starting batch polling process');
        startBatchPolling();
      } else {
        log(
          `Batch polling already active with ${Object.keys(pendingMediaPollsRef.current).length} items, skipping restart`,
        );
      }
    },
    [isBatchPollingActive, startBatchPolling],
  );

  /**
   * Legacy pollMediaStatus function - used only for virtual tours now
   */
  const pollMediaStatus = useCallback(
    async (
      mediaKey: string,
      _metadata: MediaMetadata,
    ): Promise<MediaUploadResult | null> => {
      try {
        const result = await executeQuery<any>(`CWA_Media('${mediaKey}')`);

        if (result) {
          // For virtual tours, no processing needed
          return translateMediaRecord(result, translation);
        }

        return null;
      } catch (error) {
        console.error('Error polling media status:', error);
        return null;
      }
    },
    [executeQuery, translation],
  );

  const uploadMedia = useCallback(
    async (metadata: MediaMetadata): Promise<void> => {
      const fileIdentifier = metadata.name;

      try {
        updateFileStatus(fileIdentifier, { stage: 'preparing', progress: 5 });
        startProgressSimulation(fileIdentifier, 'preparing', 5, 15, 2000);

        const base64Data = metadata.file
          ? await getBase64Data(metadata.file)
          : undefined;

        // Once we have base64 data, show the preview and update progress
        const previewUrl = base64Data
          ? `data:${metadata.type};base64,${base64Data}`
          : '';

        updateFileStatus(fileIdentifier, {
          stage: 'reading',
          progress: 15,
          previewUrl,
        });
        startProgressSimulation(fileIdentifier, 'reading', 15, 25, 3000);

        setActiveUploads((prev) => new Set(prev).add(metadata.name));
        setIsAnyUploading(true);

        updateFileStatus(fileIdentifier, { stage: 'uploading', progress: 30 });
        startProgressSimulation(fileIdentifier, 'uploading', 30, 40, 3000);

        const cleanMediaType = metadata.type.includes('/')
          ? metadata.type.split('/')[1]
          : metadata.type;
        const mediaSourceFileName = getMediaSourceFileName(metadata);
        const mediaShortDescription = getMediaShortDescription(metadata);

        const mediaRecordPayload = {
          CWAPropertyType: listingDetails.CWAPropertyType,
          CWAMEDVWLISKey: listingDetails.CWALISKey,
          CWAResourceRecordKey: listingKey,
          CWAPropMediaInformationValidFlag: true,
          CWACounty: listingDetails.CWACounty,
          CWAMediaSourceFileName: mediaSourceFileName,
          CWAMediaCategory: metadata.category,
          CWAPermission: 'Public',
          CWAMediaBytes: 0,
          CWAMediaVisibility: metadata.visibility,
          CWAMediaItemNumber: metadata.itemNumber,
          CWAMediaDisplayOrder: metadata.displayOrder,
          CWAMediaShortDescription: mediaShortDescription,
          CWAMediaLongDescription:
            metadata.description === null ? null : metadata.description || '',
          CWAMediaSizeDescription: metadata.sizeDescription || 'BRIGHT_HD',
          CWAMediaSubSystemLocale: 'BRIGHT_MAIN',
          CWAMediaSystemLocale: 'BRIGHT',
          CWAMediaType: cleanMediaType,
          CWAPreferredPhotoYN: null,
          CWAMediaSourceBusinessPartner: 'BRIGHT',
          CWADocumentType: metadata.docType || null, // For OE documents
        };

        if (metadata.type === 'txt') {
          // When the type of upload is TXT we must provide the file name for RESO
          (mediaRecordPayload as any).CWAMediaFileName = metadata.name;
        }

        // Create media record with timeout and retry logic
        const mediaRecord = await retryWithExponentialBackoff(
          async () => {
            return await createMediaRecord(mediaRecordPayload);
          },
          3, // maxRetries
          1000, // initialDelay (1 second)
          2, // backoffFactor (doubles each time: 1s, 2s, 4s)
          loggerRef.current, // pass the logger
        );

        if (mediaRecord.CWAMediaKey === null) {
          console.error(
            `MEDIA_KEY is null on response from POST to CWA_Media: ${JSON.stringify(mediaRecord)}`,
          );
          throw new Error(
            `MEDIA_KEY is null on response from POST to CWA_Media: ${JSON.stringify(mediaRecord)}`,
          );
        }

        updateFileStatus(fileIdentifier, { stage: 'sending', progress: 40 });
        startProgressSimulation(fileIdentifier, 'sending', 40, 60, 5000);

        // Process image and upload content
        if (base64Data && mediaRecord.CWAMEDVWKey) {
          let processedBase64Data = base64Data;
          let blob: Blob;

          // Only process images
          if (metadata.type.startsWith('image/')) {
            try {
              // 1. Get the exif data from the image
              const resizeImgConfig = {
                file: metadata.file!,
                targetSize: {
                  width: ImageUtils.TARGET_WIDTH,
                  height: ImageUtils.TARGET_HEIGHT,
                },
              };
              const exifResponse = await getExifInfo(resizeImgConfig);
              // 2. Downscale the image if necessary, and put the exif data in the resized image.
              const result = await downscaleImage(
                base64Data,
                imageConfig,
                exifResponse,
              );
              processedBase64Data = result.base64Data;

              blob = dataURItoBlob(processedBase64Data);
            } catch (error) {
              console.warn(
                'Image processing failed, using original image:',
                error,
              );
              blob = dataURItoBlob(processedBase64Data);
            }
          } else {
            const binaryString = atob(processedBase64Data);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }

            blob = new Blob([bytes], { type: metadata.type });
          }

          // Upload the content - THIS IS THE KEY POINT AFTER WHICH WE START BATCH POLLING
          const log = loggerRef.current?.log || console.log;
          log(
            `Uploading content for ${metadata.name} (mediaKey: ${mediaRecord.CWAMEDVWKey})`,
          );

          try {
            await retryWithExponentialBackoff(
              async () => {
                return await uploadMediaContent(
                  mediaRecord.CWAMEDVWKey,
                  blob,
                  metadata.type,
                );
              },
              3, // maxRetries
              1000, // initialDelay (1 second)
              2, // backoffFactor (doubles each time: 1s, 2s, 4s)
              loggerRef.current, // pass the logger
            );

            log(
              `Content upload complete for ${metadata.name}, now moving to processing stage`,
            );
          } catch (uploadError) {
            log(
              `Failed to upload content for ${metadata.name} after all retry attempts`,
            );
            throw uploadError; // Re-throw to be handled by the existing error handling
          }

          updateFileStatus(fileIdentifier, {
            stage: 'processing',
            progress: 60,
          });
          startProgressSimulation(
            fileIdentifier,
            'processing',
            60,
            90,
            maxPollAttempts * pollInterval * 0.8,
          );

          // Add to batch polling for everything except virtual tours
          addToBatchPolling(
            mediaRecord.CWAMEDVWKey,
            metadata,
            'upload',
            fileIdentifier,
          );
        } // For virtual tours, we don't need to wait for processing
        else if (metadata.category === 'Unbranded Virtual Tour') {
          const result = await pollMediaStatus(
            mediaRecord.CWAMEDVWKey,
            metadata,
          );
          if (result) {
            updateFileStatus(
              fileIdentifier,
              { stage: 'complete', progress: 100 },
              true,
            );

            setActiveUploads((prev) => {
              const next = new Set(prev);
              next.delete(metadata.name);
              if (next.size === 0) {
                setIsAnyUploading(false);
              }
              return next;
            });
            finalizeFile(fileIdentifier);
            onUploadComplete?.(result);
          }
          return;
        }
      } catch (error) {
        // // Clear any active progress simulation
        // if (progressIntervals.current[metadata.name]) {
        //   clearInterval(progressIntervals.current[metadata.name]);
        //   delete progressIntervals.current[metadata.name];
        // }

        // If this is a MimeType error, skip showing upload error UI
        // (MediaSyncDialog will be shown instead)
        if (isMimeTypeNullError(error)) {
          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(metadata.name);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });
          return; // Exit early, don't show error UI
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        updateFileStatus(
          fileIdentifier,
          {
            stage: 'error',
            error: errorMessage,
            statusText: 'Upload failed',
            progress: 0,
          },
          true, // clear interval
        );

        setActiveUploads((prev) => {
          const next = new Set(prev);
          next.delete(metadata.name);
          if (next.size === 0) {
            setIsAnyUploading(false);
          }
          return next;
        });

        finalizeFile(fileIdentifier);

        onUploadError?.(error as Error, metadata);
      }
    },
    [
      addToBatchPolling,
      createMediaRecord,
      finalizeFile,
      getBase64Data,
      imageConfig,
      listingDetails.CWACounty,
      listingDetails.CWALISKey,
      listingDetails.CWAPropertyType,
      listingKey,
      maxPollAttempts,
      onUploadComplete,
      onUploadError,
      pollInterval,
      pollMediaStatus,
      startProgressSimulation,
      updateFileStatus,
      uploadMediaContent,
    ],
  );

  const replaceMedia = useCallback(
    async (imageData: ProcessedImageData): Promise<void> => {
      const fileName = imageData.mediaRecord.MEDIA_SOURCE_FILE_NAME;
      try {
        const log = loggerRef.current?.log || console.log;

        updateFileStatus(fileName, {
          stage: 'preparing' as UploadStage,
          progress: 10,
          previewUrl: imageData.dataURL as string,
        });
        startProgressSimulation(fileName, 'preparing', 10, 20, 2000);

        setActiveUploads((prev) => new Set(prev).add(fileName));
        setIsAnyUploading(true);

        updateFileStatus(fileName, {
          stage: 'sending' as UploadStage,
          progress: 20,
        });
        startProgressSimulation(fileName, 'sending', 20, 60, 5000);

        // Replace content via /$value endpoint (existing pattern)
        try {
          await retryWithExponentialBackoff(
            async () => {
              return await replaceMediaContent(
                imageData.mediaRecord.MED_VW_KEY,
                imageData.blob,
                imageData.file?.type,
              );
            },
            3, // maxRetries
            1000, // initialDelay (1 second)
            2, // backoffFactor (doubles each time: 1s, 2s, 4s)
            loggerRef.current, // pass the logger
          );

          log(`Content replacement complete for ${fileName}`);
        } catch (replaceError) {
          log(
            `Failed to replace content for ${fileName} after all retry attempts`,
          );
          throw replaceError;
        }

        // For photos, add to batch polling for processing status
        if (
          imageData.file?.type?.startsWith('image/') ||
          imageData.blob?.type.startsWith('image/')
        ) {
          // Update to processing stage at 60%
          updateFileStatus(fileName, {
            stage: 'processing' as UploadStage,
            progress: 60,
          });

          // Start progress simulation for processing stage (60% to 90%)
          startProgressSimulation(
            fileName,
            'processing',
            60,
            90,
            maxPollAttempts * pollInterval * 0.8,
          );

          const fileMetadata: MediaMetadata = {
            category: 'Photo',
            description: imageData.mediaRecord.MEDIA_LONG_DESCRIPTION,
            displayOrder: imageData.mediaRecord.MEDIA_DISPLAY_ORDER,
            name: fileName,
            type: imageData.file?.type as string,
            itemNumber: imageData.mediaRecord.MEDIA_ITEM_NUMBER,
            size: imageData.blob?.size as number,
            file: imageData.file as File,
          };

          // Add to batch polling with minimal metadata
          addToBatchPolling(
            imageData.mediaRecord.MED_VW_KEY,
            fileMetadata,
            'replace',
            fileName,
          );
        } else {
          updateFileStatus(
            fileName,
            {
              stage: 'complete' as UploadStage,
              progress: 100,
            },
            true, // clear interval
          );

          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(fileName);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });
          finalizeFile(fileName);

          // Call completion handler if provided
          onReplaceComplete?.(imageData.mediaRecord);
        }
      } catch (error) {
        // Clear any active progress simulation
        const fileName = imageData.mediaRecord.MEDIA_SOURCE_FILE_NAME;
        if (progressIntervals.current[fileName]) {
          clearInterval(progressIntervals.current[fileName]);
          delete progressIntervals.current[fileName];
        }

        // If this is a MimeType error, skip showing replacement error UI
        // (MediaSyncDialog will be shown instead)
        if (isMimeTypeNullError(error)) {
          setActiveUploads((prev) => {
            const next = new Set(prev);
            next.delete(fileName);
            if (next.size === 0) {
              setIsAnyUploading(false);
            }
            return next;
          });
          return; // Exit early, don't show error UI
        }

        const errorMessage =
          error instanceof Error ? error.message : 'Unknown error';

        // Handle replacement error
        updateFileStatus(
          fileName,
          {
            stage: 'error' as UploadStage,
            error: errorMessage,
            statusText: 'Replacement failed',
            progress: 0,
          },
          true,
        );

        setActiveUploads((prev) => {
          const next = new Set(prev);
          next.delete(fileName);
          if (next.size === 0) {
            setIsAnyUploading(false);
          }
          return next;
        });
        finalizeFile(fileName);
        onReplaceError?.(error as Error, imageData);
      }
    },
    [
      addToBatchPolling,
      finalizeFile,
      maxPollAttempts,
      onReplaceComplete,
      onReplaceError,
      pollInterval,
      replaceMediaContent,
      startProgressSimulation,
      updateFileStatus,
    ],
  );

  const uploadMediaBatch = useCallback(
    async (mediaItems: MediaMetadata[]) => {
      const log = loggerRef.current?.log || console.log;

      // Clear any existing intervals and pending media
      clearAllIntervals();

      // Reset the pending media polls ref (not just the state)
      pendingMediaPollsRef.current = {};

      // Reset shadow failure counts for the new batch
      shadowFailureCountsRef.current = {};

      // Set operation type for this batch
      currentOperationTypeRef.current = 'upload';

      if (mediaItems.length === 0) {
        return {
          results: [],
          totalCount: 0,
          successCount: 0,
          failureCount: 0,
        };
      }

      const initialStatuses: Record<string, UploadFileStatus> = {};
      mediaItems.forEach((item) => {
        initialStatuses[item.name] = {
          name: item.name,
          previewUrl: '',
          stage: 'waiting', // Start in waiting state
          progress: 0,
        };
      });

      setUploadFileStatuses(initialStatuses);
      setBatchSummary({
        totalFiles: mediaItems.length,
        completedFiles: 0,
        inProgressFiles: new Set(mediaItems.map((item) => item.name)),
      });

      setIsAnyUploading(true);

      startProgressSimulation(mediaItems[0]?.name, 'waiting', 0, 5, 2000);

      // Wait for any active request operations to complete before starting batch upload
      await waitForRequestCompletion('starting batch upload', mediaItems);

      // Use Promise.allSettled to let all uploads complete regardless of individual failures
      const uploadPromise = Promise.allSettled(mediaItems.map(uploadMedia));

      // Register this upload operation
      registerRequestOperation(uploadPromise);

      const results = await uploadPromise;

      // Process results after all uploads have settled
      const uploadResults = results.map((result, index) => {
        const item = mediaItems[index];

        // Return the result or error for tracking
        return {
          file: item,
          success: result.status === 'fulfilled',
          error: result.status === 'rejected' ? result.reason : undefined,
          result: result.status === 'fulfilled' ? result.value : undefined,
        };
      });

      // Check the ref not the state
      // Only stop batch polling if there are truly no pending items in the ref
      const pendingCount = Object.keys(pendingMediaPollsRef.current).length;
      if (pendingCount === 0) {
        log('No pending media items after batch upload, stopping polling');
        if (batchPollingIntervalRef.current) {
          clearInterval(batchPollingIntervalRef.current);
          batchPollingIntervalRef.current = null;
        }
        setIsAnyUploading(false);
        setIsBatchPollingActive(false);
      } else {
        log(
          `Upload complete but ${pendingCount} items still pending processing`,
        );
      }

      // Return results so component can handle successes and failures
      return {
        results: uploadResults,
        totalCount: mediaItems.length,
        successCount: uploadResults.filter((r) => r.success).length,
        failureCount: uploadResults.filter((r) => !r.success).length,
      };
    },
    [
      clearAllIntervals,
      registerRequestOperation,
      uploadMedia,
      waitForRequestCompletion,
    ],
  );

  const replaceMediaBatch = useCallback(
    async (items: BatchProcessedImageData): Promise<BatchReplaceResult> => {
      const log = loggerRef.current?.log || console.log;

      // Clear any existing intervals and pending media
      clearAllIntervals();

      // Reset the pending media polls ref (not just the state)
      pendingMediaPollsRef.current = {};

      // Reset shadow failure counts for the new batch
      shadowFailureCountsRef.current = {};

      // Set operation type for this batch
      currentOperationTypeRef.current = 'replace';

      if (items.totalProcessed === 0) {
        return {
          results: [],
          totalCount: 0,
          successCount: 0,
          failureCount: 0,
        };
      }

      const fileIdentifiers = items.processedImages.map(
        (item) => item.mediaRecord.MEDIA_SOURCE_FILE_NAME,
      );

      // Initialize status map and batch summary
      const initialStatuses: Record<string, UploadFileStatus> = {};
      fileIdentifiers.forEach((id) => {
        initialStatuses[id] = {
          name: id,
          previewUrl: '',
          stage: 'waiting',
          progress: 0,
        };
      });

      setUploadFileStatuses(initialStatuses);
      setBatchSummary({
        totalFiles: items.processedImages.length,
        completedFiles: 0,
        inProgressFiles: new Set(fileIdentifiers),
      });

      setIsAnyUploading(true);

      startProgressSimulation(
        items.processedImages[0]?.mediaRecord.MEDIA_SOURCE_FILE_NAME,
        'waiting',
        0,
        5,
        2000,
      );

      // Wait for any active request operations to complete before starting batch replacement
      await waitForRequestCompletion(
        'starting batch replacement',
        items.processedImages,
      );

      // Initialize progress with all files
      const fileName =
        items.processedImages[0]?.mediaRecord.MEDIA_SOURCE_FILE_NAME ||
        `media-${items.processedImages[0]?.mediaRecord.MEDIA_KEY}` ||
        '';
      const files = new Set<string>();
      files.add(fileName);
      setBatchSummary({
        totalFiles: items.totalProcessed,
        completedFiles: 0,
        inProgressFiles: files,
      });

      // Use Promise.allSettled to let all replacements complete regardless of individual failures
      const replacePromise = Promise.allSettled(
        items.processedImages.map(replaceMedia),
      );

      // Register this replacement operation
      registerRequestOperation(replacePromise);

      const results = await replacePromise;

      // Process results after all replacements have settled
      const replaceResults = results.map((result, index) => {
        const item = items.processedImages[index];

        // Return the result or error for tracking
        return {
          mediaKey: item.mediaRecord.MEDIA_KEY,
          success: result.status === 'fulfilled',
          error: result.status === 'rejected' ? result.reason : undefined,
          result: result.status === 'fulfilled' ? result.value : undefined,
        };
      });

      // Check the ref not the state
      // Only stop batch polling if there are truly no pending items in the ref
      const pendingCount = Object.keys(pendingMediaPollsRef.current).length;
      if (pendingCount === 0) {
        log('No pending media items after batch replacement, stopping polling');
        if (batchPollingIntervalRef.current) {
          clearInterval(batchPollingIntervalRef.current);
          batchPollingIntervalRef.current = null;
        }
        setIsAnyUploading(false);
        setIsBatchPollingActive(false);
      } else {
        log(
          `Replacement complete but ${pendingCount} items still pending processing`,
        );
      }

      // Return results so component can handle successes and failures
      return {
        results: replaceResults,
        totalCount: items.totalProcessed,
        successCount: replaceResults.filter((r) => r.success).length,
        failureCount: replaceResults.filter((r) => !r.success).length,
      };
    },
    [
      clearAllIntervals,
      registerRequestOperation,
      replaceMedia,
      waitForRequestCompletion,
    ],
  );

  // Store logger in ref if provided
  useEffect(() => {
    if (logger) {
      loggerRef.current = logger;
    }
  }, [logger]);

  // Clean up all intervals on unmount
  useEffect(() => {
    return () => {
      clearAllIntervals();
    };
  }, []);

  /**
   * Start showing the upload dialog with a processing state for cloud file downloads
   */
  const startProcessingFiles = useCallback((fileCount: number) => {
    setIsAnyUploading(true);
    const files = new Set<string>();
    files.add(`${fileCount} ${fileCount === 1 ? 'file' : 'files'}`);
    setBatchSummary({
      totalFiles: fileCount,
      completedFiles: 0,
      inProgressFiles: files,
    });
  }, []);

  return {
    uploadMedia,
    uploadMediaBatch,
    replaceMedia,
    replaceMediaBatch,
    isUploading: isAnyUploading,
    uploadProgress,
    isMediaResourceValid,
    isBatchPollingActive,
    startProcessingFiles,
  };
}
